import useIsMobile from '../../hooks/useIsMobile';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import PageHeader from '../../components/ui/PageHeader';

const inputSt = { width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };
const selectSt = { ...inputSt, cursor: 'pointer' };

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{label}</label>
    {children}
  </div>
);

const SectionCard = ({ title, children }) => (
  <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16, overflow: 'hidden' }}>
    <div style={{ padding: '13px 18px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 14, color: '#111827' }}>{title}</div>
    <div style={{ padding: 18 }}>{children}</div>
  </div>
);

const BudgetCreate = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseFileIdParam = searchParams.get('case_file_id');

  const [docCategories, setDocCategories] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [caseFiles, setCaseFiles] = useState([]);
  const [notarySettings, setNotarySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isPublic, setIsPublic] = useState(!caseFileIdParam);
  const [selectedCaseFile, setSelectedCaseFile] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState(null);

  const [form, setForm] = useState({
    budget_document_type_id: '', case_file_id: caseFileIdParam || '',
    client_name: '', client_phone: '', client_email: '', client_address: '',
    responsible_name: '', language: 'es', currency: 'mxn', show_bank_accounts: true, notes: '',
    procedure_type: '', identifier: '', procedure_date: '', deed_number: '', volume: '', tome: '',
  });

  const focusSt = e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
  const blurSt  = e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; };

  useEffect(() => {
    Promise.all([
      api.get('/budget-document-categories'),
      api.get('/budget-document-types'),
      api.get('/case-files', { params: { per_page: 100 } }),
      api.get('/notary-settings'),
    ]).then(([catsRes, typesRes, cfRes, notaryRes]) => {
      setDocCategories(catsRes.data);
      setDocTypes(typesRes.data);
      const cfs = cfRes.data.data || cfRes.data;
      setCaseFiles(cfs);
      setNotarySettings(notaryRes.data);
      if (caseFileIdParam) {
        const cf = cfs.find(c => c.id === parseInt(caseFileIdParam));
        if (cf) { setSelectedCaseFile(cf); setForm(p => ({ ...p, responsible_name: cf.assigned_user?.name || '' })); }
      }
    }).finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'budget_document_type_id') {
      const dt = docTypes.find(t => t.id === parseInt(value));
      setSelectedDocType(dt || null);
      if (dt) setForm(p => ({ ...p, show_bank_accounts: dt.show_bank_accounts }));
    }
    if (name === 'case_file_id') {
      const cf = caseFiles.find(c => c.id === parseInt(value));
      setSelectedCaseFile(cf || null);
      if (cf) setForm(p => ({ ...p, responsible_name: cf.assigned_user?.name || '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.budget_document_type_id) { toast.error('Selecciona un tipo de documento'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (isPublic) payload.case_file_id = null;
      const res = await api.post('/budgets', payload);
      toast.success('Presupuesto creado');
      navigate(`/budgets/${res.data.id}`);
    } catch { toast.error('Error al crear presupuesto'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );

  const typesByCategory = docCategories.map(cat => ({
    ...cat, types: docTypes.filter(t => t.budget_document_category_id === cat.id),
  })).filter(cat => cat.types.length > 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontSize: 12, color: '#9CA3AF' }}>
        <Link to="/budgets" style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>Presupuestos</Link>
        <span>›</span>
        <span style={{ color: '#4B5563' }}>Nuevo Presupuesto</span>
      </div>

      <PageHeader
        title="Nuevo Presupuesto"
        subtitle="Completa los datos para crear el presupuesto"
        actions={
          <Link to="/budgets" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Regresar
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap: 16, alignItems: 'start' }}>

          {/* Columna principal */}
          <div>
            {/* Tipo de documento */}
            <SectionCard title="Tipo de Documento">
              <Field label="Tipo de Documento *">
                <select name="budget_document_type_id" value={form.budget_document_type_id} onChange={handleChange} required style={selectSt} onFocus={focusSt} onBlur={blurSt}>
                  <option value="">Seleccionar...</option>
                  {typesByCategory.map(cat => (
                    <optgroup key={cat.id} label={cat.name}>
                      {cat.types.map(type => (
                        <option key={type.id} value={type.id}>{type.series ? `[${type.series}] ` : ''}{type.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
              {selectedDocType && (
                <div style={{ marginTop: 4, padding: '8px 12px', borderRadius: 8, backgroundColor: '#EFF6FF', border: '1px solid #DBEAFE', fontSize: 12, color: '#1D4ED8' }}>
                  <strong>Serie:</strong> {selectedDocType.series || '—'} &nbsp;·&nbsp;
                  <strong>Siguiente folio:</strong> {selectedDocType.series ? `${selectedDocType.series}-${selectedDocType.folio_sequence}` : selectedDocType.folio_sequence} &nbsp;·&nbsp;
                  <strong>Conceptos:</strong> {selectedDocType.concept_categories?.reduce((a, c) => a + (c.concepts?.length || 0), 0) || 0}
                </div>
              )}
            </SectionCard>

            {/* Cliente */}
            <SectionCard title="Cliente">
              {/* Toggle público/expediente con descripción */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  {[
                    { label: '📁 Vincular a Expediente', active: !isPublic, onClick: () => setIsPublic(false) },
                    { label: '👤 Público en General',    active: isPublic,  onClick: () => { setIsPublic(true); setForm(p => ({ ...p, case_file_id: '' })); setSelectedCaseFile(null); } },
                  ].map(btn => (
                    <button key={btn.label} type="button" onClick={btn.onClick}
                      style={{ padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${btn.active ? '#4F63D2' : '#E5E7EB'}`, backgroundColor: btn.active ? '#4F63D2' : '#fff', color: btn.active ? '#fff' : '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s' }}>
                      {btn.label}
                    </button>
                  ))}
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                  {isPublic
                    ? '👤 El presupuesto se genera sin vincularlo a ningún expediente ni cliente del sistema. Úsalo para cotizaciones rápidas o clientes ocasionales.'
                    : '📁 El presupuesto queda ligado a un expediente existente. Los pagos y el saldo pendiente aparecerán en Cuentas por Cobrar.'}
                </p>
              </div>

              {!isPublic ? (
                <Field label="Expediente">
                  <select name="case_file_id" value={form.case_file_id} onChange={handleChange} style={selectSt} onFocus={focusSt} onBlur={blurSt}>
                    <option value="">Seleccionar expediente...</option>
                    {caseFiles.map(cf => <option key={cf.id} value={cf.id}>{cf.client?.name} — {cf.title}</option>)}
                  </select>
                  {selectedCaseFile && (
                    <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', fontSize: 12, color: '#4B5563' }}>
                      <strong>{selectedCaseFile.client?.name}</strong>
                      {selectedCaseFile.client?.phone && ` · ${selectedCaseFile.client.phone}`}
                      {selectedCaseFile.client?.email && ` · ${selectedCaseFile.client.email}`}
                    </div>
                  )}
                </Field>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <Field label="Nombre del cliente">
                    <input type="text" name="client_name" value={form.client_name} onChange={handleChange} placeholder="PÚBLICO EN GENERAL" style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                  </Field>
                  <Field label="Teléfono">
                    <input type="text" name="client_phone" value={form.client_phone} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                  </Field>
                  <Field label="Correo">
                    <input type="email" name="client_email" value={form.client_email} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                  </Field>
                  <Field label="Dirección">
                    <input type="text" name="client_address" value={form.client_address} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                  </Field>
                </div>
              )}

              <Field label="Abogado / Responsable">
                <input type="text" name="responsible_name" value={form.responsible_name} onChange={handleChange} placeholder={notarySettings?.notary_name || ''} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
              </Field>
            </SectionCard>

            {/* Datos del trámite */}
            <SectionCard title="Datos del Trámite (opcional)">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Field label="Tipo de Trámite">
                  <input type="text" name="procedure_type" value={form.procedure_type} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </Field>
                <Field label="Identificador">
                  <input type="text" name="identifier" value={form.identifier} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </Field>
                <Field label="Fecha">
                  <input type="date" name="procedure_date" value={form.procedure_date} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </Field>
                <Field label="Escritura">
                  <input type="text" name="deed_number" value={form.deed_number} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </Field>
                <Field label="Volumen">
                  <input type="text" name="volume" value={form.volume} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </Field>
                <Field label="Tomo">
                  <input type="text" name="tome" value={form.tome} onChange={handleChange} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </Field>
              </div>
            </SectionCard>

            {/* Notas */}
            <SectionCard title="Notas">
              <textarea name="notes" rows={3} value={form.notes} onChange={handleChange} placeholder="Observaciones adicionales..." style={{ ...inputSt, resize: 'vertical' }} onFocus={focusSt} onBlur={blurSt} />
            </SectionCard>
          </div>

          {/* Columna derecha */}
          <div>
            <SectionCard title="Configuración">
              <Field label="Idioma">
                <select name="language" value={form.language} onChange={handleChange} style={selectSt}>
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </Field>
              <Field label="Moneda">
                <select name="currency" value={form.currency} onChange={handleChange} style={selectSt}>
                  <option value="mxn">Pesos MXN</option>
                  <option value="usd">Dólares USD</option>
                </select>
              </Field>
              {form.currency === 'usd' && (
                <div style={{ padding: '8px 12px', borderRadius: 8, backgroundColor: '#EFF6FF', border: '1px solid #DBEAFE', fontSize: 12, color: '#1D4ED8', marginBottom: 14 }}>
                  Tipo de cambio: <strong>${notarySettings?.usd_exchange_rate} MXN/USD</strong>
                  <br />Puedes ajustarlo en el detalle.
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4B5563', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" name="show_bank_accounts" checked={form.show_bank_accounts} onChange={handleChange} style={{ width: 15, height: 15, accentColor: '#4F63D2' }} />
                Mostrar cuentas bancarias en PDF
              </label>
            </SectionCard>

            {/* Conceptos previos + total estimado */}
            {selectedDocType?.concept_categories?.length > 0 && (() => {
              const allConcepts = selectedDocType.concept_categories.flatMap(c => c.concepts || []);
              const estimatedTotal = allConcepts.reduce((sum, c) => {
                const base = Number(c.default_amount) || 0;
                const iva  = c.includes_iva ? base * ((c.iva_rate || 16) / 100) : 0;
                return sum + base + iva;
              }, 0);
              return (
                <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16, overflow: 'hidden' }}>
                  <div style={{ padding: '13px 18px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 14, color: '#111827' }}>Conceptos que se cargarán</div>
                  {selectedDocType.concept_categories.map(cat => (
                    <div key={cat.id}>
                      <div style={{ padding: '7px 14px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cat.name}</span>
                      </div>
                      {cat.concepts?.map(c => (
                        <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 14px', borderBottom: '1px solid #F9FAFB' }}>
                          <span style={{ fontSize: 12, color: '#6B7280' }}>{c.name}</span>
                          <div style={{ textAlign: 'right' }}>
                            {c.type === 'general' ? (
                              <span style={{ fontSize: 12, fontStyle: 'italic', color: '#9CA3AF' }}>
                                {c.default_text_value || 'se llena después'}
                              </span>
                            ) : (
                              <>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                                  ${Number(c.default_amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </span>
                                {c.includes_iva && (
                                  <span style={{ display: 'block', fontSize: 10, color: '#9CA3AF' }}>
                                    + IVA {c.iva_rate || 16}%
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  {/* Total estimado */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 18px', backgroundColor: '#EEF0FB', borderTop: '2px solid #C7D2F8' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Total estimado</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#4F63D2' }}>
                      ${estimatedTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })} {form.currency?.toUpperCase()}
                    </span>
                  </div>
                  <p style={{ margin: 0, padding: '6px 14px 10px', fontSize: 11, color: '#9CA3AF' }}>
                    Estimado base — ajustable después de crear el presupuesto.
                  </p>
                </div>
              );
            })()}

            {/* Botones */}
            <button type="submit" disabled={submitting}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 14, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1, marginBottom: 8 }}>
              {submitting ? 'Creando...' : 'Crear Presupuesto'}
            </button>
            <Link to="/budgets" style={{ display: 'block', width: '100%', padding: '10px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, textDecoration: 'none', textAlign: 'center' }}>
              Cancelar
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BudgetCreate;
